import type { LocationQuery, LocationQueryRaw } from 'vue-router'

type ChromeMessageResponse = {
  success?: boolean
  error?: string
}

type ChromeRuntimeLike = {
  lastError?: {
    message?: string
  }
  sendMessage?: (
    extensionId: string,
    message: unknown,
    responseCallback: (response?: ChromeMessageResponse) => void,
  ) => void
}

type WindowWithChromeRuntime = Window & {
  chrome?: {
    runtime?: ChromeRuntimeLike
  }
}

const EXTENSION_AUTH_KEYS = [
  'source',
  'client',
  'extensionId',
  'redirect_uri',
  'callbackUrl',
  'return_to',
  'redirect',
] as const

function firstQueryValue(value: LocationQuery[string]): string {
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === 'string') || ''
  }
  return typeof value === 'string' ? value : ''
}

function getRedirectUrl(query: LocationQuery): string {
  return (
    firstQueryValue(query.redirect_uri) ||
    firstQueryValue(query.callbackUrl) ||
    firstQueryValue(query.return_to)
  )
}

function withTokenInHash(url: string, token: string): string {
  const target = new URL(url)
  const hashParams = new URLSearchParams(target.hash.replace(/^#/, ''))
  hashParams.set('token', token)
  target.hash = hashParams.toString()
  return target.toString()
}

async function sendTokenToExtension(extensionId: string, token: string): Promise<boolean> {
  const runtime = (window as WindowWithChromeRuntime).chrome?.runtime
  if (!runtime?.sendMessage) {
    return false
  }

  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(false), 3000)

    try {
      runtime.sendMessage(
        extensionId,
        {
          type: 'ACCOUNT_CENTER_AUTH_SUCCESS',
          action: 'accountCenterAuthSuccess',
          source: 'account-center',
          token,
          accessToken: token,
        },
        (response) => {
          window.clearTimeout(timeout)
          if (runtime.lastError) {
            resolve(false)
            return
          }
          resolve(response?.success === true)
        },
      )
    } catch {
      window.clearTimeout(timeout)
      resolve(false)
    }
  })
}

export function isExtensionAuthRequest(query: LocationQuery): boolean {
  const extensionId = firstQueryValue(query.extensionId)
  const redirectUrl = getRedirectUrl(query)
  return (
    firstQueryValue(query.source) === 'afsa-stellar-extension' ||
    firstQueryValue(query.client) === 'afsa-stellar' ||
    (!!extensionId && !!redirectUrl)
  )
}

export function buildAuthRoute(path: '/login' | '/register', query: LocationQuery) {
  if (!isExtensionAuthRequest(query)) {
    return path
  }

  const nextQuery: LocationQueryRaw = {}
  for (const key of EXTENSION_AUTH_KEYS) {
    const value = query[key]
    if (value !== undefined && value !== null) {
      nextQuery[key] = value
    }
  }

  return {
    path,
    query: nextQuery,
  }
}

export async function completeExtensionAuth(query: LocationQuery, token: string): Promise<boolean> {
  if (!isExtensionAuthRequest(query)) {
    return false
  }

  const extensionId = firstQueryValue(query.extensionId)
  if (extensionId && (await sendTokenToExtension(extensionId, token))) {
    window.setTimeout(() => window.close(), 600)
    return true
  }

  const redirectUrl = getRedirectUrl(query)
  if (redirectUrl) {
    window.location.replace(withTokenInHash(redirectUrl, token))
    return true
  }

  return false
}
